import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiImage, FiPlus, FiEdit2, FiTrash2, FiCheck, FiX,
  FiUpload, FiBarChart2, FiSave, FiEye, FiEyeOff,
  FiRefreshCw, FiExternalLink, FiLayout,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { clearSiteSettingsCache } from '../hooks/useSiteSettings';

/* ── Shared styles ──────────────────────────────────────────────── */
const S = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: '24px',
    marginBottom: 24,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: '1px solid #f3f4f6',
  },
  cardTitle: { fontWeight: 700, fontSize: 15, margin: 0, color: '#111827' },
  cardSub: { color: '#9ca3af', fontSize: 12, marginTop: 3 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '9px 12px',
    border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none',
    fontFamily: 'inherit',
  },
  btnPrimary: {
    background: '#0078BF', color: '#fff', border: 'none', borderRadius: 8,
    padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  btnGhost: {
    background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8,
    padding: '7px 14px', fontWeight: 500, fontSize: 13, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 5, color: '#374151',
  },
  btnDanger: {
    background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8,
    padding: '7px 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 5,
  },
  tag: (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 600,
    background: active ? '#dcfce7' : '#f3f4f6',
    color: active ? '#16a34a' : '#9ca3af',
  }),
};

/* ── Upload helper ─────────────────────────────────────────────── */
const uploadImage = async (file) => {
  const form = new FormData();
  form.append('image', file);
  const { data } = await API.post('/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!data.success) throw new Error(data.message || 'Upload failed');
  return { url: data.url, filename: data.filename };
};

/* ════════════════════════════════════════════════════════════════
   REUSABLE SECTION IMAGE CARD
   Used for Hero, Our Story, and Why Choose Us images.

   Props:
   - settingsKey  : e.g. 'about_us_hero'
   - imageField   : key inside the settings object, e.g. 'heroImage' or 'image'
   - title        : card heading
   - description  : card sub-heading
   - recommendedSize : hint shown below the upload button
   ════════════════════════════════════════════════════════════════ */
const SectionImageCard = ({
  settingsKey,
  imageField,
  title,
  description,
  recommendedSize,
  successMessage,
}) => {
  const [imgUrl, setImgUrl]       = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    API.get(`/site-settings/public/${settingsKey}`)
      .then(({ data }) => setImgUrl(data[settingsKey]?.[imageField] || ''))
      .catch(() => {});
  }, [settingsKey, imageField]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setImgUrl(url);
      toast.success('Image uploaded — click Save to apply');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put(`/site-settings/${settingsKey}`, { [imageField]: imgUrl });
      clearSiteSettingsCache(settingsKey);
      toast.success(successMessage || `${title} saved`);
    } catch {
      toast.error('Failed to save image');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <div style={S.cardTitle}>{title}</div>
          <div style={S.cardSub}>{description}</div>
        </div>
        <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>
          {saving ? <FiRefreshCw size={13} /> : <FiSave size={13} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Preview box */}
        <div style={{
          width: 220, height: 148, borderRadius: 10, overflow: 'hidden',
          border: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {imgUrl
            ? <img src={imgUrl} alt={`${title} preview`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ textAlign: 'center', color: '#d1d5db' }}>
                <FiImage size={32} />
                <div style={{ fontSize: 11, marginTop: 6 }}>No image set</div>
              </div>
          }
        </div>

        {/* Controls */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>Image URL</label>
            <input
              style={S.input}
              value={imgUrl}
              onChange={(e) => setImgUrl(e.target.value)}
              placeholder="https://cdn.example.com/image.webp"
            />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              style={S.btnGhost}
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <FiRefreshCw size={13} /> : <FiUpload size={13} />}
              {uploading ? 'Uploading…' : 'Upload Image'}
            </button>
            {imgUrl && (
              <>
                <a
                  href={imgUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...S.btnGhost, textDecoration: 'none' }}
                >
                  <FiExternalLink size={13} /> Preview
                </a>
                <button style={S.btnDanger} onClick={() => setImgUrl('')}>
                  <FiX size={13} /> Clear
                </button>
              </>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
          <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 12 }}>
            {recommendedSize || 'Recommended: 700×525px or larger (4:3 ratio). PNG or JPG. Max 5 MB.'}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   STAT FORM MODAL
   ════════════════════════════════════════════════════════════════ */
const EMPTY_STAT = { value: '', label: '', icon: '', isActive: true };

const StatModal = ({ stat, onClose, onSave }) => {
  const [form, setForm] = useState(stat ? { ...stat } : { ...EMPTY_STAT });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async () => {
    if (!form.value.trim()) return toast.error('Value is required (e.g. 500+)');
    if (!form.label.trim()) return toast.error('Label is required (e.g. Happy Customers)');
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch {
      // error toasted in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 28,
        width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>
            {stat ? 'Edit Statistic' : 'Add Statistic'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={S.label}>Value <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              style={S.input}
              name="value"
              value={form.value}
              onChange={handleChange}
              placeholder="e.g. 500+  or  1,000+  or  100%"
              autoFocus
            />
          </div>
          <div>
            <label style={S.label}>Label <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              style={S.input}
              name="label"
              value={form.label}
              onChange={handleChange}
              placeholder="e.g. Happy Customers"
            />
          </div>
          <div>
            <label style={S.label}>Icon (optional emoji or symbol)</label>
            <input
              style={S.input}
              name="icon"
              value={form.icon}
              onChange={handleChange}
              placeholder="e.g. 🎯  or  ✅  or leave blank"
            />
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              Paste an emoji. Leave blank to show only the number.
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              style={{ width: 16, height: 16, accentColor: '#0078BF' }}
            />
            Active (visible on About Us page)
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          <button style={S.btnGhost} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary} onClick={handleSubmit} disabled={saving}>
            {saving ? <FiRefreshCw size={13} /> : <FiCheck size={13} />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   NUMBERS MANAGEMENT CARD
   ════════════════════════════════════════════════════════════════ */
const NumbersCard = () => {
  const [stats, setStats]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const [deleting, setDeleting] = useState(null);
  const dragIdx = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/about-stats');
      setStats(data.stats || []);
    } catch {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (form.id) {
        await API.put(`/about-stats/${form.id}`, form);
        toast.success('Statistic updated');
      } else {
        await API.post('/about-stats', form);
        toast.success('Statistic added');
      }
      load();
    } catch {
      toast.error('Failed to save statistic');
      throw new Error('save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this statistic?')) return;
    setDeleting(id);
    try {
      await API.delete(`/about-stats/${id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggle = async (stat) => {
    try {
      await API.put(`/about-stats/${stat.id}`, { ...stat, isActive: !stat.isActive });
      setStats((s) => s.map((x) => x.id === stat.id ? { ...x, isActive: !x.isActive } : x));
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDragStart = (idx) => { dragIdx.current = idx; };
  const handleDrop = async (dropIdx) => {
    if (dragIdx.current === null || dragIdx.current === dropIdx) return;
    const reordered = [...stats];
    const [moved] = reordered.splice(dragIdx.current, 1);
    reordered.splice(dropIdx, 0, moved);
    dragIdx.current = null;
    setStats(reordered);
    try {
      await API.patch('/about-stats/reorder', {
        order: reordered.map((s, i) => ({ id: s.id, displayOrder: i + 1 })),
      });
    } catch {
      toast.error('Failed to save order');
      load();
    }
  };

  return (
    <>
      {modal && (
        <StatModal
          stat={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      <div style={S.card}>
        <div style={S.cardHeader}>
          <div>
            <div style={S.cardTitle}>Our Numbers / Impact</div>
            <div style={S.cardSub}>
              Statistics shown in the "Numbers That Matter" section. Drag rows to reorder.
            </div>
          </div>
          <button style={S.btnPrimary} onClick={() => setModal('add')}>
            <FiPlus size={14} /> Add Stat
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
            Loading…
          </div>
        ) : stats.length === 0 ? (
          <div style={{
            padding: '40px 24px', textAlign: 'center', background: '#f9fafb',
            borderRadius: 10, border: '1px dashed #e5e7eb',
          }}>
            <FiBarChart2 size={32} color="#d1d5db" style={{ marginBottom: 12 }} />
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>No statistics yet</div>
            <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 16 }}>
              Add your first stat — e.g. "500+" / "Happy Customers"
            </div>
            <button style={S.btnPrimary} onClick={() => setModal('add')}>
              <FiPlus size={13} /> Add First Stat
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {stats.map((stat, idx) => (
              <div
                key={stat.id}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(idx)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  background: '#fff', border: '1px solid #e5e7eb',
                  borderRadius: 10, padding: '12px 16px',
                  cursor: 'grab', userSelect: 'none',
                }}
              >
                <span style={{ color: '#d1d5db', fontSize: 18, flexShrink: 0 }}>⠿</span>
                {stat.icon && <span style={{ fontSize: 20, flexShrink: 0 }}>{stat.icon}</span>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 17, color: '#111827', lineHeight: 1 }}>
                    {stat.value}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{stat.label}</div>
                </div>
                <span style={S.tag(stat.isActive)}>
                  {stat.isActive ? 'Active' : 'Hidden'}
                </span>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button title={stat.isActive ? 'Hide' : 'Show'} style={{ ...S.btnGhost, padding: '6px 10px' }} onClick={() => handleToggle(stat)}>
                    {stat.isActive ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                  </button>
                  <button title="Edit" style={{ ...S.btnGhost, padding: '6px 10px' }} onClick={() => setModal(stat)}>
                    <FiEdit2 size={14} />
                  </button>
                  <button title="Delete" style={{ ...S.btnDanger, padding: '6px 10px' }} onClick={() => handleDelete(stat.id)} disabled={deleting === stat.id}>
                    {deleting === stat.id ? <FiRefreshCw size={14} /> : <FiTrash2 size={14} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

/* ════════════════════════════════════════════════════════════════
   OUR CLIENTS NOTE CARD
   ════════════════════════════════════════════════════════════════ */
const ClientsNoteCard = () => (
  <div style={{ ...S.card, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <FiImage size={20} color="#0078BF" style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#0c4a6e', marginBottom: 4 }}>
          Our Clients — Managed Separately
        </div>
        <p style={{ fontSize: 13, color: '#0369a1', lineHeight: 1.6, margin: 0 }}>
          Client logos shown on the About Us page are the same logos managed under{' '}
          <strong>Our Clients</strong> in the sidebar. Changes there appear on both
          the Home page and the About Us page automatically.
        </p>
        <a
          href="/admin/client-logos"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, color: '#0078BF', fontWeight: 600, fontSize: 13 }}
        >
          Go to Client Logos <FiExternalLink size={13} />
        </a>
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════ */
const AdminAboutUs = () => {
  const [tab, setTab] = useState('images');

  const tabs = [
    { key: 'images',  label: 'Section Images', icon: <FiLayout size={14} /> },
    { key: 'numbers', label: 'Our Numbers',     icon: <FiBarChart2 size={14} /> },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 4 }}>About Us</h2>
        <p style={{ color: '#6b7280', fontSize: '0.88rem' }}>
          Manage the dynamic content shown on the public About Us page.
        </p>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 4,
        background: '#f3f4f6', borderRadius: 10,
        padding: 4, marginBottom: 28,
        width: 'fit-content',
      }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '8px 18px', borderRadius: 8, border: 'none',
              fontWeight: 600, fontSize: 13, cursor: 'pointer',
              background: tab === t.key ? '#fff' : 'transparent',
              color: tab === t.key ? '#111827' : '#6b7280',
              boxShadow: tab === t.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Images tab: Hero + Our Story + Why Choose Us ── */}
      {tab === 'images' && (
        <>
          <SectionImageCard
            settingsKey="about_us_hero"
            imageField="heroImage"
            title="Hero Image"
            description="Appears in the right column of the About Us hero section (top of page)."
            recommendedSize="Recommended: 900×675px or larger (4:3 ratio). PNG or JPG. Max 5 MB."
            successMessage="Hero image saved"
          />

          <SectionImageCard
            settingsKey="about_us_story"
            imageField="image"
            title="Our Story Image"
            description='Appears on the left side of the "Your Trusted Printing Partner" section.'
            recommendedSize="Recommended: 700×525px or larger (4:3 ratio). PNG or JPG. Max 5 MB."
            successMessage="Our Story image saved"
          />

          <SectionImageCard
            settingsKey="about_us_why"
            imageField="image"
            title="Why Choose Us Image"
            description='Appears on the right side of the "The Angel Printers Difference" section.'
            recommendedSize="Recommended: 700×525px or larger (4:3 ratio). PNG or JPG. Max 5 MB."
            successMessage="Why Choose Us image saved"
          />

          <div style={{
            background: '#fffbeb', border: '1px solid #fde68a',
            borderRadius: 10, padding: '14px 18px', fontSize: 13, color: '#92400e',
          }}>
            <strong>Note:</strong> All text content, headings, descriptions, buttons, and layout
            are part of the page design. Only these section images are admin-controlled.
          </div>
        </>
      )}

      {/* ── Numbers tab ── */}
      {tab === 'numbers' && (
        <>
          <NumbersCard />
          <ClientsNoteCard />
        </>
      )}
    </div>
  );
};

export default AdminAboutUs;